import React, { useCallback, useMemo, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, CornerDownRight, Minimize2, Wand2 } from 'lucide-react';
import {
  checkJson, countLines, excerptLine, formatJson, gutterText, lineRange, minifyJson,
  JsonError, JsonRewrite,
} from './jsonEditing';

/**
 * A raw-JSON textarea that says where the fault is, and can reformat itself.
 *
 * <h3>Still a textarea</h3>
 * No editor component is pulled in for this. The three that would do it
 * (Monaco, CodeMirror, Ace) each weigh more than this whole app's bundle,
 * which CRA already calls oversized — and the two things they are wanted for
 * here, a location for the error and re-indenting, are `JSON.parse`'s own
 * error and `JSON.stringify(v, null, 2)`. So the box stays a textarea and
 * gains a gutter beside it.
 *
 * <h3>How the gutter stays aligned</h3>
 * A textarea has no gutter, so the numbers live in a second element that is
 * scrolled in step with it. That only holds if one line of text is one row on
 * screen, which is why the textarea does not soft-wrap: with wrapping on, a
 * long prompt occupies three rows and every number below it points at the
 * wrong line — worse than having none. Long lines scroll sideways instead.
 * The numbers are one text node, not one node per line, so a 2,500 line
 * document costs one DOM update and only when the line COUNT changes.
 *
 * <h3>The one rule for callers</h3>
 * This holds no text of its own. Every change — typing, Format, Minify —
 * leaves through the single `onChange` prop, so a parent that invalidates
 * something on edit (the runtime editor drops its computed diff) cannot be
 * fooled by a reformat: reformatting IS an edit, by construction, not by
 * remembering to handle it.
 */

/** Locked to both halves in px so the numbers cannot drift from the lines. */
const LINE_HEIGHT = 18;
const FONT_SIZE = 11;
const PAD_Y = 10;

/**
 * The error for `text`, recomputed only when the text changes.
 *
 * Blank is reported as no error: an empty box is "nothing typed yet" in both
 * editors — the tool editor sends `{}` for it and the runtime editor has
 * nothing to compare — not a mistake to shout about while someone clears the
 * field to paste.
 */
export function useJsonCheck(text: string): JsonError | null {
  return useMemo(() => (text.trim() === '' ? null : checkJson(text)), [text]);
}

interface Props {
  value: string;
  /** The only way text leaves this component. Typing and Format share it. */
  onChange: (next: string) => void;
  /** From `useJsonCheck(value)` in the parent, which usually needs it too. */
  error: JsonError | null;
  /** Height of the editing box in px. */
  height?: number;
  placeholder?: string;
  /** Shown to the right of the buttons, e.g. what the field is forwarded as. */
  hint?: React.ReactNode;
}

export default function JsonTextArea({ value, onChange, error, height = 448, placeholder, hint }: Props) {
  const taRef = useRef<HTMLTextAreaElement>(null);
  const gutterRef = useRef<HTMLDivElement>(null);
  const [refused, setRefused] = useState<string | null>(null);

  const lines = useMemo(() => countLines(value), [value]);
  // Keyed on the COUNT, so typing inside a line rebuilds nothing.
  const numbers = useMemo(() => gutterText(lines), [lines]);

  const syncScroll = useCallback(() => {
    const ta = taRef.current;
    const gutter = gutterRef.current;
    if (ta && gutter) gutter.scrollTop = ta.scrollTop;
  }, []);

  const change = (next: string) => { setRefused(null); onChange(next); };

  const rewrite = (fn: (t: string) => JsonRewrite, name: string) => {
    if (!value.trim()) { setRefused(`There is nothing to ${name.toLowerCase()}.`); return; }
    const result = fn(value);
    if (!result.ok) {
      const at = result.error.at;
      setRefused(
        at
          ? `${name} needs valid JSON — fix line ${at.line} first. Nothing was changed.`
          : `${name} needs valid JSON. Nothing was changed.`,
      );
      return;
    }
    // Always through onChange, even when the text is unchanged: a parent that
    // drops state on edit must see this as an edit, and "it happened to be
    // identical" is not a distinction worth a branch here.
    change(result.text);
  };

  const jumpTo = (line: number) => {
    const ta = taRef.current;
    if (!ta) return;
    const { start, end } = lineRange(value, line);
    ta.focus();
    ta.setSelectionRange(start, end);
    ta.scrollTop = Math.max(0, (line - 1) * LINE_HEIGHT - ta.clientHeight / 3);
    syncScroll();
  };

  const monospace: React.CSSProperties = {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: FONT_SIZE,
    lineHeight: `${LINE_HEIGHT}px`,
    paddingTop: PAD_Y,
    paddingBottom: PAD_Y,
    tabSize: 2,
  };

  const btn = 'inline-flex items-center gap-1.5 rounded border border-gray-300 dark:border-gray-600 px-2 py-1 ' +
              'text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40';

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => rewrite(t => formatJson(t, 2), 'Format')} className={btn}>
          <Wand2 className="w-3.5 h-3.5" /> Format
        </button>
        <button type="button" onClick={() => rewrite(minifyJson, 'Minify')} className={btn}>
          <Minimize2 className="w-3.5 h-3.5" /> Minify
        </button>

        {value.trim() === '' ? (
          <span className="text-xs text-gray-400">Empty</span>
        ) : error ? (
          <span className="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
            <AlertCircle className="w-3.5 h-3.5" />
            {error.at ? `Line ${error.at.line}, column ${error.at.column}` : 'Not valid JSON'}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
            <CheckCircle2 className="w-3.5 h-3.5" /> Valid JSON
          </span>
        )}

        <span className="text-xs text-gray-400">{lines.toLocaleString()} lines</span>
        {hint && <span className="text-xs text-gray-400">{hint}</span>}
      </div>

      {refused && (
        <div className="rounded bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
          {refused}
        </div>
      )}

      {error && value.trim() !== '' && <ErrorPanel error={error} onJump={jumpTo} />}

      <div
        className="flex overflow-hidden rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
        style={{ height }}
      >
        <div
          ref={gutterRef}
          aria-hidden
          className="select-none overflow-hidden border-r border-gray-200 dark:border-gray-700
                     bg-gray-50 dark:bg-gray-900/40 px-2 text-right text-gray-400 dark:text-gray-500"
          style={{ ...monospace, whiteSpace: 'pre', minWidth: `${String(lines).length + 1}ch` }}
        >
          {numbers}
        </div>
        <textarea
          ref={taRef}
          value={value}
          onChange={e => change(e.target.value)}
          onScroll={syncScroll}
          wrap="off"
          spellCheck={false}
          placeholder={placeholder}
          className="flex-1 resize-none border-0 bg-transparent px-3 text-gray-900 dark:text-gray-100
                     outline-none focus:ring-0"
          style={{ ...monospace, whiteSpace: 'pre', overflowWrap: 'normal' }}
        />
      </div>
    </div>
  );
}

/** What is wrong, where, and the line itself with a caret under the column. */
function ErrorPanel({ error, onJump }: { error: JsonError; onJump: (line: number) => void }) {
  const at = error.at;
  const excerpt = at ? excerptLine(at.lineText, at.column) : null;
  // Tabs are kept as tabs in the caret's padding so it lands on the same tab
  // stop the character did; everything else becomes a space.
  const caretPad = excerpt ? excerpt.text.slice(0, excerpt.column - 1).replace(/[^\t]/g, ' ') : '';

  return (
    <div className="rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-2 text-xs">
      <div className="flex flex-wrap items-baseline gap-x-2 text-red-700 dark:text-red-300">
        {at && <span className="font-medium">Line {at.line}, column {at.column}:</span>}
        <span>{error.message}</span>
        {at && (
          <button type="button" onClick={() => onJump(at.line)}
                  className="inline-flex items-center gap-1 text-red-700 dark:text-red-300 underline">
            <CornerDownRight className="w-3 h-3" /> go to it
          </button>
        )}
      </div>

      {excerpt && (
        <pre className="mt-1.5 overflow-x-auto rounded bg-white/70 dark:bg-black/30 p-2 font-mono text-[11px]
                        leading-tight text-gray-700 dark:text-gray-200">
{(excerpt.truncatedLeft ? '…' : '') + excerpt.text + (excerpt.truncatedRight ? '…' : '')}
{'\n'}{(excerpt.truncatedLeft ? ' ' : '') + caretPad}<span className="text-red-600 dark:text-red-400">^</span>
        </pre>
      )}

      {/* Kept verbatim: when our wording is worse than the engine's, or a
          future V8 says something we did not anticipate, this is the truth. */}
      <div className="mt-1.5 text-[11px] text-red-500/80 dark:text-red-400/70">
        The browser said: {error.raw}
      </div>
    </div>
  );
}
